import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [organization, setOrganization] = useState('')
  const [isSignup, setIsSignup] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const {
    user,
    loading,
    login,
    loginWithGoogle,
    signup,
    error,
    needsProfile,
    completeProfile,
  } = useAuth()

  // Redirect if already logged in
  if (user && !needsProfile) {
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    if (isSignup) {
      await signup(email, password, firstName, lastName, organization)
    } else {
      await login(email, password)
    }
    setSubmitting(false)
  }

  const handleGoogleLogin = async () => {
    setSubmitting(true)
    await loginWithGoogle()
    setSubmitting(false)
  }

  const handleCompleteProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    await completeProfile(firstName, lastName, organization)
    setSubmitting(false)
  }

  const isLoading = loading || submitting

  // Show "Complete Your Profile" form for Google sign-in users missing info
  if (needsProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <h1 className="text-2xl mb-2 text-center">Complete Your Profile</h1>
          <p className="text-sm text-[var(--color-text-secondary)] text-center mb-8">
            Tell us a bit about yourself to get started.
          </p>

          <form onSubmit={handleCompleteProfile} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="profile-first" className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  First Name
                </label>
                <input
                  id="profile-first"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="input"
                  required
                />
              </div>
              <div>
                <label htmlFor="profile-last" className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Last Name
                </label>
                <input
                  id="profile-last"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="input"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="profile-org" className="block text-sm text-[var(--color-text-secondary)] mb-1">
                Organization
              </label>
              <input
                id="profile-org"
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                className="input"
                required
                placeholder="University, company, etc."
              />
            </div>

            {error && (
              <p className="text-sm text-red-400">{error}</p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full"
            >
              {isLoading ? 'Loading...' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl mb-8 text-center">Portal</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isSignup && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-sm text-[var(--color-text-secondary)] mb-1">
                    First Name
                  </label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm text-[var(--color-text-secondary)] mb-1">
                    Last Name
                  </label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="input"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="organization" className="block text-sm text-[var(--color-text-secondary)] mb-1">
                  Organization
                </label>
                <input
                  id="organization"
                  type="text"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  className="input"
                  required
                  placeholder="University, company, etc."
                />
              </div>
            </>
          )}

          <div>
            <label htmlFor="email" className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm text-[var(--color-text-secondary)] mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
              autoComplete={isSignup ? 'new-password' : 'current-password'}
            />
          </div>

          {error && (
            <p className="text-sm text-red-400">{error}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? 'Loading...' : isSignup ? 'Sign Up' : 'Log In'}
          </button>
        </form>

        <div className="my-6 flex items-center gap-4">
          <div className="flex-1 border-t border-[var(--color-border)]" />
          <span className="text-sm text-[var(--color-text-tertiary)]">or</span>
          <div className="flex-1 border-t border-[var(--color-border)]" />
        </div>

        <button
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="btn btn-secondary w-full"
        >
          {isLoading ? 'Loading...' : 'Continue with Google'}
        </button>

        <p className="mt-6 text-center text-sm text-[var(--color-text-secondary)]">
          {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button
            type="button"
            onClick={() => setIsSignup(!isSignup)}
            className="text-[var(--color-text)] underline"
          >
            {isSignup ? 'Log in' : 'Sign up'}
          </button>
        </p>
      </div>
    </div>
  )
}
